import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import useAxios from "../../utils/useAxios";
import AuthContext from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import LocationForm from "./LocationForm";
import useModal from "../../hooks/useModal";
import MenuList from "../../components/MenuList";
import { getInitials } from "../../constants";
import debounce from "lodash.debounce";
import { prefetchUrl } from "../../utils/tabPrefetch";
import useCompactToolbar from "../../hooks/useCompactToolbar";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";

const LocationsList = ({ is_authorized = true }) => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const isSuperAdmin = !!user?.is_superuser;
    // own locations stay manageable by their creator; locations shared from
    // the intennis pool (is_intennis_db) can only be managed by a superadmin
    const canManage = (location) => isSuperAdmin || location.creator === user?.user_id;
    const history = useHistory();
    const routerLocation = useLocation();
    const authedApi = useAxios();
    // see ClubsList.js for why this isn't just useAxios(): its token would
    // still identify a logged-in admin even while viewing the public
    // "Перегляд" list, scoping ?all=true to their own data instead of
    // everyone's.
    const api = is_authorized ? authedApi : axios;
    const { modalIsOpen, setIsOpen, openModal } = useModal(false);
    const { ref, inView } = useInView({ threshold: 1 });
    const searchInputRef = useRef(null);
    const requestIdRef = useRef(0);
    // Compact Table is a user-facing-only density preference - an admin on
    // /admin/locations still gets the regular card view regardless of the
    // stored setting, only seeing it once they switch to Public View for
    // this same list. See Header.js/BottomNav.js for the matching
    // toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;

    const [locations, setLocations] = useState([]);
    const [currentPage, setCurrentPage] = useState("");
    const [countLocations, setCountLocations] = useState();
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(10);
    const [editingLocation, setEditingLocation] = useState(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        document.title = t("Locations");
        resetAndFetch();
    }, []);

    // See ClubsList.js's own copy of this same formula for the full story:
    // a Compact Table row runs ~40px tall, so the backend's own default
    // page size (sized for cards) leaves a compact list several screens
    // short of a full viewport, forcing extra scroll-triggered fetches
    // just to fill it once.
    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    const resetAndFetch = (query = "") => {
        requestIdRef.current += 1;
        setLocations([]);
        setSkeletonCards(10);
        setHasError(false);
        const params = new URLSearchParams();
        if (query && query.length >= 2) params.set("search", query);
        // public page should show locations across all organizers, not just
        // whatever the endpoint defaults to for an unauthenticated request
        if (!is_authorized) params.set("all", "true");
        if (compactTable) params.set("limit", compactPageSize());
        setCurrentPage(`/api/v1.0/locations/${params.toString() ? "?" + params.toString() : ""}`);
    };

    const debouncedSearch = useMemo(() => debounce((val) => resetAndFetch(val), 400), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        if ((inView || locations.length === 0) && currentPage && !loader && !hasError) {
            getLocations();
        }
    }, [inView, currentPage, loader, hasError]);

    const getLocations = async () => {
        const reqId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await prefetchUrl(api, currentPage);
            if (reqId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            if (!countLocations) {
                setCountLocations(totalCount);
            }

            setLocations(prev => {
                const existingIds = new Set(prev.map(l => l.id));
                const newItems = results.filter(l => !existingIds.has(l.id));
                return [...prev, ...newItems];
            });
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const updatedCount = locations.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(10, remaining) : 0);
        } catch (error) {
            if (reqId !== requestIdRef.current) return;
            console.error(error);
            setHasError(true);
        } finally {
            if (reqId === requestIdRef.current) {
                setLoader(false);
            }
        }
    };

    const handleRetry = () => {
        setHasError(false);
        getLocations();
    };

    const handleCreateClick = () => {
        setEditingLocation(null);
        openModal();
    };

    const handleItemClick = (location) => {
        const params = new URLSearchParams(routerLocation.search);
        params.set("location_id", location.id);
        history.push({ search: params.toString() });
    };

    const { toolbarSentinelRef, isCompact: isToolbarCompact, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        },
        onCreate: is_authorized ? handleCreateClick : null
    });

    return (
        <Container className="page_container">
            <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
            {/* Navigation */}
            <div className={`sticky-toolbar ${isToolbarCompact ? "compact" : ""} ${!toolbarActivated ? "never-opened" : ""}`}>
                <nav className="tabs-container toolbar-search-row">
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" />
                            <input
                                ref={searchInputRef}
                                className="search"
                                type="text"
                                placeholder={t("Locations search: name...")}
                                onKeyUp={e => debouncedSearch(e.target.value)}
                                onBlur={e => { if (!e.target.value) collapseToolbar(); }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </li>
                    </ul>
                </nav>
            </div>

            {compactTable ? (
                <div className="trows">
                    {locations.map((location, idx) => (
                        <CompactTableRow
                            key={location.id || idx}
                            variant="entity"
                            zebra={idx % 2 === 1}
                            avatarSrc={location.thumbnail}
                            title={location.name}
                            subtitle={[location.city, location.country_name].filter(Boolean).join(", ")}
                            onClick={() => handleItemClick(location)}
                            actions={is_authorized && canManage(location) && (
                                <MenuList
                                    deleteEndpoint={`/api/v1.0/locations/${location.id}`}
                                    onDeleted={() => setLocations(prev => prev.filter(l => l.id !== location.id))}
                                    successMessage="Location deleted successfully"
                                    errorMessage="Error deleting location"
                                    is_edit
                                    onEdit={() => {
                                        setEditingLocation(location);
                                        openModal();
                                    }}
                                    editText={t("Edit location")}
                                    title={location.name}
                                    titleIcon="map-pin"
                                    asModal
                                />
                            )}
                        />
                    ))}
                    {(loader || (currentPage && locations.length === 0)) && skeletonCards > 0 && (
                        Array.from({ length: skeletonCards }).map((_, idx) => (
                            <div className="trow trow-entity" key={`skel-loc-${idx}`} style={{ pointerEvents: "none" }}>
                                <Skeleton circle width={20} height={20} />
                                <Skeleton width="60%" height={12} />
                                <span />
                            </div>
                        ))
                    )}
                </div>
            ) : (
            <div className="entity-cards">
                {locations.map((location, idx) => (
                    <div className="entity-card" key={location.id || idx} onClick={() => handleItemClick(location)}>
                        {is_authorized && canManage(location) && (
                            <div className="entity-card-actions" onClick={e => e.stopPropagation()}>
                                <MenuList
                                    deleteEndpoint={`/api/v1.0/locations/${location.id}`}
                                    onDeleted={() => setLocations(prev => prev.filter(l => l.id !== location.id))}
                                    successMessage="Location deleted successfully"
                                    errorMessage="Error deleting location"
                                    is_edit
                                    onEdit={() => {
                                        setEditingLocation(location);
                                        openModal();
                                    }}
                                    editText={t("Edit location")}
                                    title={location.name}
                                    titleIcon="map-pin"
                                    asModal
                                />
                            </div>
                        )}
                        <div className="entity-card-top">
                            <div className="entity-card-thumb">
                                {location.thumbnail
                                    ? <img src={location.thumbnail} alt="" />
                                    : <div className="no-thumbnail initials">{getInitials(location.name)}</div>
                                }
                            </div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title">{location.name}</div>
                                <div className="entity-card-subtitle">
                                    {location.city}
                                    {location.city && location.country_name && ", "}
                                    {location.country_name}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {(loader || (currentPage && locations.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                    <div className="entity-card" key={`skel-loc-${idx}`} style={{ pointerEvents: "none" }}>
                        <div className="entity-card-top">
                            <div className="entity-card-thumb" style={{ background: "transparent" }}>
                                <Skeleton circle width={52} height={52} />
                            </div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title"><Skeleton width="65%" height={15} /></div>
                                <div className="entity-card-subtitle"><Skeleton width="45%" height={12} style={{ marginTop: 4 }} /></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            )}

            {!loader && hasError && (
                <div className="not-found">
                    <h2><FeatherIcon icon="alert-triangle" size={106}/></h2>
                    <h4>{t("Something went wrong")}</h4>
                    <p>{t("Failed to load list. Please check your connection and try again.")}</p>
                    <a href="#" className="action-button btn-green" onClick={(e) => { e.preventDefault(); handleRetry(); }}>{t("Try again")}</a>
                </div>
            )}

            {is_authorized && (
                <CustomModal isOpen={modalIsOpen} onClose={() => setIsOpen(false)}>
                    <LocationForm
                        onClose={() => setIsOpen(false)}
                        locationId={editingLocation?.id || null}
                        img={editingLocation?.image || null}
                        width={editingLocation?.width || null}
                        height={editingLocation?.height || null}
                        onSuccess={(newItem) => {
                            if (!newItem?.id) {
                                setLocations(prev => prev.filter(loc => loc.id !== editingLocation?.id));
                                setIsOpen(false);
                                return;
                            }
                            setLocations(prev => {
                                const index = prev.findIndex(loc => loc.id === newItem.id);
                                if (index !== -1) {
                                    const updated = [...prev];
                                    updated[index] = newItem;
                                    return updated;
                                } else {
                                    return [newItem, ...prev];
                                }
                            });
                            setIsOpen(false);
                        }}
                    />
                </CustomModal>
            )}
            <div ref={ref}></div>
        </Container>
    );
};

export default LocationsList;
